import React, {useEffect, useMemo, useRef, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputCheckbox from "../../../../components/input/input-checkbox.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import Loader from "../../../../components/template/loader.jsx";
import DispatchService from "../../../../services/dispatch/dispatch-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import {DASHBOARD_QUERY_KEYS} from "../utils/dashboard-query-keys.js";
import {useDashboardContext} from "../context/dashboard-context.js";
import useDashboardModalState from "../hooks/use-dashboard-modal-state.js";
import DispatchRefuelling from "../../dispatch/form/components/dispatch-refuelling.jsx";
import DispatchMaintenance from "../../dispatch/form/components/dispatch-maintenance.jsx";
import {navigateHook} from "../../../../hooks/use-navigate.js";

const DESPACHO_WEB_URL = import.meta.env.VITE_API_DESPACHO_WEB;

const REFUELLING_NATURE = "Abastecimento";
const MAINTENANCE_NATURE = "Manutenção";

const STEP_TITLES = {
    main: "Encerrar despacho",
    refuelling: "Abastecimento",
    maintenance: "Manutenção",
    supports: "Encerrar despacho de apoio",
};

const CloseDispatchModal = ({payload, onClose, onSuccess}) => {
    const navigate = navigateHook();
    const {configuration} = useDashboardContext();
    const [finalMileage, setFinalMileage] = useState("");
    const [nature, setNature] = useState("");
    const [providence, setProvidence] = useState("");
    const [finalObservation, setFinalObservation] = useState("");
    const [cancelSupport, setCancelSupport] = useState(false);
    const [flags, setFlags] = useState({});
    const [supportRows, setSupportRows] = useState([]);
    const [mileageWarning, setMileageWarning] = useState(null);
    const [saveInvalidMileage, setSaveInvalidMileage] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);
    const refuellingRef = useRef(null);
    const maintenanceRef = useRef(null);
    const requested = Boolean(payload);
    const garrison = payload?.garrison;
    const garrisonId = garrison?.garrisonId;
    const dispatchType = garrison?.garrisonType;

    const closeQuery = useQuery({
        queryKey: DASHBOARD_QUERY_KEYS.closedDispatch(garrisonId),
        queryFn: () => DispatchService.getClosedDispatch(garrisonId),
        enabled: requested && Boolean(garrisonId),
    });
    const isReady = !closeQuery.isFetching && (closeQuery.isSuccess || closeQuery.isError);
    const {open, closeModal, isPreparing} = useDashboardModalState({payload, isReady, onClose});

    useEffect(() => {
        if (!requested) return;
        setFinalMileage("");
        setNature("");
        setProvidence("");
        setFinalObservation("");
        setCancelSupport(false);
        setFlags({});
        setMileageWarning(null);
        setSaveInvalidMileage(false);
        setIsSubmitting(false);
        setStepIndex(0);
    }, [requested]);

    useEffect(() => {
        if (closeQuery.data?.dispatchNature) {
            setNature(closeQuery.data.dispatchNature);
        }
    }, [closeQuery.data?.dispatchNature]);

    useEffect(() => {
        const modalGarrison = closeQuery.data?.garrison;
        if (!requested || !configuration.isSuggestKm || !modalGarrison?.kmControl || modalGarrison.actualKm === undefined || modalGarrison.actualKm === null) return;
        setFinalMileage(String(modalGarrison.actualKm));
    }, [closeQuery.data?.garrison, configuration.isSuggestKm, requested]);

    useEffect(() => {
        if (!requested) return;
        const list = closeQuery.data?.listSupport ?? [];
        setSupportRows(list.map(item => ({
            dispatchId: item.dispatchId,
            garrisonName: item.garrisonName,
            kmControl: Boolean(item.kmControl),
            finalMileage: configuration.isSuggestKm && item.kmControl && item.actualKm !== undefined && item.actualKm !== null
                ? String(item.actualKm)
                : "",
            finalObservation: "",
        })));
    }, [configuration.isSuggestKm, requested, closeQuery.data]);

    const result = closeQuery.data ?? {};
    const modalGarrison = result.garrison;
    const kmControl = Boolean(modalGarrison?.kmControl);
    const isSupport = dispatchType === "dispatchSupport";
    const isActivity = dispatchType === "dispatchActivity";
    const hasVehicle = (result?.compositionGarrison ?? []).some(item => item.type === 'WITH_VEHICLE');
    const hasSupportToClose = supportRows.length > 0;
    const isRefuelling = nature === REFUELLING_NATURE;
    const isMaintenance = nature === MAINTENANCE_NATURE;
    const showRefuellingStep = hasVehicle && isRefuelling;
    const showMaintenanceStep = hasVehicle && isMaintenance;

    const steps = useMemo(() => {
        const list = ["main"];
        if (showRefuellingStep) list.push("refuelling");
        if (showMaintenanceStep) list.push("maintenance");
        if (hasSupportToClose) list.push("supports");
        return list;
    }, [showRefuellingStep, showMaintenanceStep, hasSupportToClose]);

    const currentStep = steps[Math.min(stepIndex, steps.length - 1)];
    const isLastStep = stepIndex >= steps.length - 1;

    const natureOptions = useMemo(() => {
        const options = (result.natures ?? []).map(item => ({
            value: item.name ?? item.alias,
            label: item.alias ?? item.name,
            occurrence: item.occurrence,
        }));

        if (result.dispatchNature && !options.some(option => option.value === result.dispatchNature)) {
            options.push({value: result.dispatchNature, label: result.dispatchNature});
        }

        return options;
    }, [result.dispatchNature, result.natures]);

    const selectedNatureOption = natureOptions.find(option => option.value === nature);
    const providenceOptions = useMemo(() => (result.providences ?? []).map(item => ({
        value: item.name ?? item.value ?? item,
        label: item.label ?? item.name ?? item,
    })), [result.providences]);

    const setExclusiveFlag = (key, value, exclusiveKey) => {
        setFlags(prev => ({
            ...prev,
            [key]: value,
            ...(value && {[exclusiveKey]: false}),
        }));
    };
    const setPoliceAuthority = (value) => {
        setFlags(prev => ({
            ...prev,
            policeAuthority: value,
            ...(!value && {
                infracionalAct: false,
                notCriminal: false,
                flagrant: false,
                indicted: false,
            }),
        }));
    };
    const setPoliceAuthorityFlag = (key, value) => {
        setFlags(prev => ({
            ...prev,
            [key]: value,
            ...(value && {policeAuthority: true}),
        }));
    };
    const updateSupport = (index, partial) => setSupportRows(prev => prev.map((row, i) => i === index ? {...row, ...partial} : row));

    const validateMainStep = async () => {
        if (!result.dispatchId) {
            projectUtils.swalErrorMessage("", "O despacho selecionado já foi encerrado", "Ok");
            return false;
        }

        if (!nature) {
            projectUtils.swalErrorMessage("", "Selecione uma natureza.", "Ok");
            return false;
        }

        if (selectedNatureOption?.occurrence === "MANDATORY" && !result.occurrenceNumber) {
            projectUtils.swalErrorMessage("", "É obrigatório gerar número de BO para esta natureza.", "Ok");
            return false;
        }

        if (kmControl && finalMileage !== "") {
            const mileageResult = await DispatchService.checkMileage(garrisonId, finalMileage);
            if (mileageResult?.invalid && !saveInvalidMileage) {
                setMileageWarning({actualKm: mileageResult.actualKm ?? "0"});
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);

        try {
            if (steps.length === 1) {
                const isValid = await validateMainStep();
                if (!isValid) {
                    setIsSubmitting(false);
                    return;
                }
            }

            let refuellingList = [];
            let maintenanceList = [];
            let files = [];

            if (showRefuellingStep && refuellingRef.current) {
                const isRefuellingValid = refuellingRef.current.validate();
                if (!isRefuellingValid) {
                    setStepIndex(steps.indexOf("refuelling"));
                    setIsSubmitting(false);
                    return;
                }

                const collected = refuellingRef.current.collect();
                refuellingList = collected.list ?? [];
                files = [...files, ...(collected.files ?? [])];
            }

            if (showMaintenanceStep && maintenanceRef.current) {
                const isMaintenanceValid = maintenanceRef.current.validate();
                if (!isMaintenanceValid) {
                    setStepIndex(steps.indexOf("maintenance"));
                    setIsSubmitting(false);
                    return;
                }

                const collected = maintenanceRef.current.collect();
                maintenanceList = collected.list ?? [];
                files = [...files, ...(collected.files ?? [])];
            }

            const closePayload = {
                dispatchId: result.dispatchId,
                finalMileage: finalMileage || undefined,
                providence,
                nature: nature?.value ?? nature,
                finalObservation,
                cancelSupport,
                flagrant: !!flags.flagrant,
                infracionalAct: !!flags.infracionalAct,
                publicPatrimony: !!flags.publicPatrimony,
                notFound: !!flags.notFound,
                policeAuthority: !!flags.policeAuthority,
                indicted: !!flags.indicted,
                notCriminal: !!flags.notCriminal,
                refuellingList,
                maintenanceList,
            };

            if (hasSupportToClose) {
                closePayload.supports = supportRows.map(row => ({
                    dispatchId: row.dispatchId,
                    finalMileage: row.finalMileage || undefined,
                    finalObservation: row.finalObservation || undefined,
                    cancelSupport: false,
                }));
            }

            const response = await DispatchService.closeDispatch(closePayload, files, hasSupportToClose);

            if (response === "BAD_REQUEST" || !response) {
                projectUtils.swalErrorMessage("", "Não foi possível encerrar o despacho", "Ok");
                setIsSubmitting(false);
                return;
            }

            closeModal();

            const checkbook = response.numberCheckbook ? ` Talão ${response.numberCheckbook}.` : "";
            const attendanceId = result.attendance?.id;
            const attendanceIdToClose = attendanceId !== undefined && attendanceId !== "" && attendanceId != null && result.taskId == null
                ? attendanceId
                : null;
            const closedGarrisonId = response.garrison?.id;

            projectUtils.swalSuccessMessage(
                "success",
                "Sucesso",
                `Despacho encerrado com sucesso${checkbook}`,
                "Ok",
                true,
                "Editar",
                ({isDismissed}) => {
                    if (isDismissed) {
                        navigate(`${DESPACHO_WEB_URL}/dispatch/form/${result.dispatchId}?redir=despacho/dashboard`, true);
                    }

                    onSuccess({attendanceId: attendanceIdToClose, garrisonId: closedGarrisonId});
                },
                false,
                false,
                "#fff",
                {cancelButton: "swal-edit-button"},
            );
        } catch (error) {
            console.error("Erro ao encerrar despacho pelo dashboard:", error);
            const message = error.response?.data ?? "Não foi possível encerrar o despacho";
            projectUtils.swalErrorMessage("", message, "Ok");
            setIsSubmitting(false);
        }
    };

    const handleNext = async () => {
        if (currentStep === "main") {
            const isValid = await validateMainStep();
            if (!isValid) return;
        }
        setStepIndex(index => index + 1);
    };

    const stepClass = (key) => currentStep === key ? "flex flex-col gap-4" : "hidden";
    const modalWidth = (showRefuellingStep || showMaintenanceStep) ? "w-7/12" : "w-6/12";
    const disableActions = closeQuery.isError || closeQuery.isLoading;

    return (
        <>
            {isPreparing && <Loader/>}
            <Modal open={open} setOpen={closeModal} title={STEP_TITLES[currentStep] ?? "Encerrar despacho"} className={modalWidth} isCloseClick={false} animated={false}>
                <Modal.Body className="relative max-h-[calc(100vh-180px)] flex flex-col gap-4 overflow-auto">
                    {closeQuery.isError && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">Não foi possível carregar os dados do despacho.</div>}
                    {!closeQuery.isError && (
                        <>
                            <div className={stepClass("main")}>
                                <div><p className="text-[14px] font-bold text-[#676a6c]">Guarnição</p><span className="text-sm text-[#555]">{modalGarrison?.name ?? garrison?.garrisonName}</span></div>
                                {result.showMessageForUnuploadedBo && (
                                    <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{result.showMessageForUnuploadedBo}</div>
                                )}
                                {!isSupport && !isActivity && <SelectComponent classNameWrapper="w-full" label="Natureza *" value={nature} options={natureOptions} onChange={selected => setNature(selected?.value ?? "")}/>}
                                {kmControl && <InputForm integerOnly label="Km final" type="number" value={finalMileage} min={1} onChange={({target}) => { setFinalMileage(target.value); setMileageWarning(null); setSaveInvalidMileage(false); }}/>}
                                <InputTextarea label="Observação final" value={finalObservation} onChange={({target}) => setFinalObservation(target.value)} height={120}/>
                                {!isSupport && !isActivity && <SelectComponent classNameWrapper="w-full" label="Providência" value={providence} options={providenceOptions} onChange={selected => setProvidence(selected?.value ?? "")}/>}
                                {!isSupport && !isActivity && (
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        {[
                                            ["notFound", "publicPatrimony", "Não constatado"],
                                            ["publicPatrimony", "notFound", "Patrimônio público"],
                                        ].map(([key, exclusiveKey, label]) => (
                                            <InputCheckbox key={key} id={`dispatch-${key}`} label={label} checked={!!flags[key]} onChange={({target}) => setExclusiveFlag(key, target.checked, exclusiveKey)}/>
                                        ))}
                                        <div className="col-span-2">
                                            <InputCheckbox id="dispatch-policeAuthority" label="Autoridade policial?" checked={!!flags.policeAuthority} onChange={({target}) => setPoliceAuthority(target.checked)}/>
                                        </div>
                                        <div className="col-span-2 ml-6 grid grid-cols-2 gap-2">
                                            {[
                                                ["infracionalAct", "Ato infracional"],
                                                ["notCriminal", "Não criminal"],
                                                ["flagrant", "Flagrante"],
                                                ["indicted", "Indiciado"],
                                            ].map(([key, label]) => (
                                                <InputCheckbox key={key} id={`dispatch-${key}`} label={label} checked={!!flags[key]} onChange={({target}) => setPoliceAuthorityFlag(key, target.checked)}/>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {isSupport && <InputCheckbox id="dashboard-cancel-support" label="Cancelar apoio" checked={cancelSupport} onChange={({target}) => setCancelSupport(target.checked)}/>}
                                {mileageWarning && (
                                    <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                        <div className="mb-2 text-center">O km informado é menor que o atual. Deseja continuar?<br/>Km atual: <b>{mileageWarning.actualKm}</b></div>
                                        <InputCheckbox id="dispatch-save-invalid-mileage" label="Sim, continuar mesmo assim" checked={saveInvalidMileage} onChange={({target}) => setSaveInvalidMileage(target.checked)}/>
                                    </div>
                                )}
                            </div>

                            {showRefuellingStep && (
                                <div className={stepClass("refuelling")}>
                                    <DispatchRefuelling ref={refuellingRef} finalMileage={finalMileage} isSuggestKm={configuration.isSuggestKm} place={result.dispatchPlace} dispatchId={result.dispatchId} garrisonId={garrisonId} showLocation title={false}/>
                                </div>
                            )}

                            {showMaintenanceStep && (
                                <div className={stepClass("maintenance")}>
                                    <DispatchMaintenance ref={maintenanceRef} finalMileage={finalMileage} isSuggestKm={configuration.isSuggestKm} place={result.dispatchPlace} dispatchId={result.dispatchId} garrisonId={garrisonId} showLocation title={false}/>
                                </div>
                            )}

                            {hasSupportToClose && (
                                <div className={stepClass("supports")}>
                                    {supportRows.map((row, index) => (
                                        <div key={row.dispatchId} className="flex flex-col gap-2 rounded border border-[#ededed] p-3">
                                            <span className="text-sm font-semibold text-[#555]">{row.garrisonName}</span>
                                            {row.kmControl && <InputForm integerOnly label="Km final" type="number" value={row.finalMileage} min={1} onChange={({target}) => updateSupport(index, {finalMileage: target.value})}/>}
                                            <InputTextarea label="Observação final" value={row.finalObservation} onChange={({target}) => updateSupport(index, {finalObservation: target.value})} height={90}/>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {stepIndex > 0 && <Button type="button" target="secondary" onClick={() => setStepIndex(index => index - 1)} disabled={isSubmitting}>Voltar</Button>}
                    {isLastStep
                        ? <Button type="button" onClick={handleSubmit} loading={isSubmitting} disabled={disableActions}>Salvar</Button>
                        : <Button type="button" onClick={handleNext} disabled={disableActions}>Próximo</Button>}
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CloseDispatchModal;